// src/views/Organizations/OrganizationsView.jsx
"use client";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import GlassPanel from "../../components/ui/GlassPanel";
import Spinner from "../../components/Spinner";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { PageHeader } from "../../components/dashboard/PageHeader";
import AppBackdrop from "../../components/ui/AppBackdrop";
import { 
  Building2, 
  Users, 
  Settings, 
  Plus, 
  Shield,
  Crown,
  Eye,
  Edit,
  Mail,
  Globe,
  Calendar,
  XCircle,
  Copy,
  Download
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useOrganizations } from "../../hooks/useOrganizations";
import AccountService from "../../services/account.service";
import OrganizationService from "../../services/organization.service";
import { resolveAccountDomainError } from "../../services/accountDomainErrorMessages";
import OrganizationMembers from "../Settings/org/OrganizationMembers";

/**
 * Organizations Management View
 * Gestión completa de organizaciones, members y proyectos
 * Integrado con OrganizationContext
 */
export default function OrganizationsView() {
  const navigate = useNavigate();
  const {
    organizations,
    organizationsLoading,
    personalAccount,
    createOrganization,
    updateOrganization,
    loadOrganizations,
    error
  } = useOrganizations();
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loadingTier, setLoadingTier] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [createFormError, setCreateFormError] = useState(null);
  const [editFormError, setEditFormError] = useState(null);
  const [newOrgApiKey, setNewOrgApiKey] = useState(null);
  const [showNewOrgKeyDialog, setShowNewOrgKeyDialog] = useState(false);

  // Cargar información del tier cuando el personalAccount esté disponible
  useEffect(() => {
    const loadTierInfo = async () => {
      if (personalAccount?.id) {
        console.log('🔍 Loading tier for account:', personalAccount.id);
        setLoadingTier(true);
        const result = await AccountService.getAccountTier(personalAccount.id);
        console.log('📊 Tier result:', result);
        if (result.success) {
          setTierInfo(result.data);
          console.log('✅ Tier loaded:', result.data);
        } else {
          console.error('❌ Failed to load tier:', result.error);
        }
        setLoadingTier(false);
      }
    };
    loadTierInfo();
  }, [personalAccount?.id]);

  const handleCreateOrg = async (formData) => {
    // Filtrar campos vacíos antes de enviar
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value && value.toString().trim() !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    console.log('📤 Sending organization data:', cleanedData);
    const result = await createOrganization(cleanedData);
    if (result.success) {
      setCreateFormError(null);
      setCreateModalOpen(false);
      
      if (result.data?.api_key) {
        setNewOrgApiKey(result.data.api_key);
        setShowNewOrgKeyDialog(true);
      }
    } else {
      const mapped = resolveAccountDomainError({ code: result.code, context: result.context });
      const statusLabel =
        result.status === 422 ? 'Validation error' :
        result.status === 409 ? 'Organization already exists' :
        result.status === 403 ? 'Permission denied' :
        'Failed to create organization';
      const detail = result.error?.join(', ');
      setCreateFormError(mapped || (detail ? `${statusLabel}: ${detail}` : statusLabel));
    }
  };

  const handleEditOrg = (organization) => {
    console.log('✏️ Editing organization:', organization);
    setSelectedOrg(organization);
    setEditModalOpen(true);
  };

  const handleUpdateOrg = async (formData) => {
    if (!selectedOrg) return;
    
    // Filtrar campos vacíos
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value && value.toString().trim() !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    console.log('📤 Updating organization:', selectedOrg.id, cleanedData);
    const result = await updateOrganization(selectedOrg.id, cleanedData);
    
    if (result.success) {
      setEditFormError(null);
      setEditModalOpen(false);
      setSelectedOrg(null);
      // Refetch, no `window.location.reload()`: recargar la app entera para ver
      // un nombre nuevo tira el contexto y hace parpadear toda la pantalla.
      await loadOrganizations();
    } else {
      const mapped = resolveAccountDomainError({ code: result.code, context: result.context });
      const statusLabel =
        result.status === 422 ? 'Validation error' :
        result.status === 403 ? 'You do not have permission to update this organization' :
        result.status === 404 ? 'Organization not found' :
        'Failed to update organization';
      const detail = result.error?.join(', ');
      setEditFormError(mapped || (detail ? `${statusLabel}: ${detail}` : statusLabel));
    }
  };

  const handleDeleteOrg = (orgId) => {
    setDeactivateConfirm(orgId);
  };

  const confirmDeactivate = async () => {
    const orgId = deactivateConfirm;
    setDeactivateConfirm(null);
    
    console.log('🔄 Closing organization:', orgId);
    
    const result = await OrganizationService.closeOrganization(orgId);
    
    console.log('Result:', result);
    
    if (result.success) {
      console.log('✅ Organization closed successfully');
      setStatusMessage({
        type: 'success',
        text: `Organization closed successfully! ${result.data.memberships_deactivated || 0} memberships and ${result.data.accounts_deactivated || 0} accounts deactivated.`
      });
      // Recargar organizaciones sin refrescar la página
      await loadOrganizations();
      setTimeout(() => setStatusMessage(null), 5000);
    } else {
      console.error('❌ Failed to close organization:', result.error);
      const mapped = resolveAccountDomainError({ code: result.code, context: result.context });
      const statusLabel =
        result.status === 403 ? 'You do not have permission to close this organization' :
        result.status === 404 ? 'Organization not found' :
        result.status === 422 ? 'Validation error' :
        'Failed to close organization';
      const detail = result.error?.join(', ');
      setStatusMessage({
        type: 'error',
        text: mapped || (detail ? `${statusLabel}: ${detail}` : statusLabel)
      });
      setTimeout(() => setStatusMessage(null), 5000);
    }
  };

  const cancelDeactivate = () => {
    setDeactivateConfirm(null);
  };

  // Administrar una organización tiene URL propia. Era un modal a pantalla
  // completa: no se podía enlazar, recargar perdía el sitio, y el botón de atrás
  // del navegador salía de la app en vez de cerrar la pantalla.
  const handleSelectOrg = (organization) => {
    navigate(`/o/${organization.id}/overview`);
  };

  return (
    // El mismo armazon que Support y las tarifas, que son las otras dos que
    // cuelgan del rail: la seccion pone el fondo, `AppBackdrop` el halo y el
    // contenido va en una capa aparte por encima. Esta pintaba negro plano, y
    // al cambiar de pantalla se notaba el corte.
    //
    // Sin `animate-blur-reveal`. `blurReveal` termina en `filter: blur(0)` y
    // corre con `forwards`, asi que la seccion se quedaba con un `filter`
    // aplicado para siempre —computado, `blur(0px)`— y un filtro obliga a
    // pintar todo lo de adentro como una sola capa. En escritorio son dos
    // columnas y la pagina mide la mitad; de pie es una columna y con varias
    // organizaciones se va a miles de pixeles, y ahi la capa fallaba a
    // pedazos: tarjetas del alto correcto y con el contenido sin dibujar.
    //
    // La entrada no se pierde: la cabecera, la cuenta personal y las tarjetas
    // traen la suya, y esas animan `opacity` y `transform`, que no dejan nada
    // puesto al terminar.
    <section className="relative min-h-screen overflow-hidden bg-gray-950 text-white">
      <AppBackdrop />
      {/* `rail-pad` hace dos trabajos y por eso no se hardcodea ninguno: aparta
          el contenido del rail en escritorio y, debajo de `md`, le deja al
          final el hueco de la barra de navegacion para que la ultima tarjeta no
          quede tapada.

          Va SOLA en su elemento. Cualquier utilidad de padding de Tailwind en
          el mismo nodo —`p-8`, y tambien `px-8`— escribe `padding-left` y la
          pisa, porque `output.css` se importa despues de `App.css`: medido, el
          rail queda en 32px en vez de 224 y el contenido se mete debajo. El
          margen propio va en un hijo, que es tambien donde puede cambiar por
          breakpoint sin tocar el desplazamiento del rail.

          Ponerle `!important` al `padding-left` no sirve: en movil `--rail-w`
          vale 0 y entonces se comeria el margen lateral entero. */}
      <div className="relative z-10 rail-pad">
      <div className="p-4 sm:p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Status Message Toast */}
        {statusMessage && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg border animate-in slide-in-from-top-2 ${
            statusMessage.type === 'success' 
              ? 'bg-green-900/90 border-green-500 text-green-100' 
              : 'bg-red-900/90 border-red-500 text-red-100'
          }`}>
            <div className="flex items-center gap-2">
              {statusMessage.type === 'success' ? '✅' : '❌'}
              <span>{statusMessage.text}</span>
            </div>
          </div>
        )}

        {/* Cabecera. El boton de volver se fue con la carcasa: la pantalla vive
            dentro del shell, asi que la navegacion la llevan el rail en
            escritorio y la barra de abajo con su cajon en movil. Un boton que
            siempre iba a "/" duplicaba eso, y en movil era ademas la unica
            salida que habia.

            La accion no va en el hueco de `PageHeader` sino al lado: ahi es
            `shrink-0`, y "Create Organization" son ~180px que en un telefono le
            dejaban al titulo menos de la mitad de la linea. Aca cae debajo y a
            lo ancho, que es lo que corresponde a la accion principal de una
            pantalla que por lo demas se recorre. Se renderiza una sola vez. */}
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          {/* La animacion va en el envoltorio: `PageHeader` recibe `className`
              pero no `style`, y el retardo se habria perdido sin avisar. */}
          <div
            className="min-w-0 flex-1 opacity-0 animate-reveal-up [animation-fill-mode:forwards]"
            style={{ animationDelay: '0.15s' }}
          >
            <PageHeader
              title="Organizations & Accounts"
              subtitle="Manage your personal account, organizations, teams, and collaborative workspaces"
            />
          </div>

          <Dialog open={createModalOpen} onOpenChange={(open) => { setCreateModalOpen(open); if (!open) setCreateFormError(null); }}>
            <DialogTrigger asChild>
              <Button className="w-full md:w-auto shrink-0 bg-primary hover:bg-violet-700 text-white transition-all duration-300 md:hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 opacity-0 animate-reveal-right [animation-fill-mode:forwards]" style={{ animationDelay: '0.15s' }}>
                <Plus className="w-4 h-4 mr-2" />
                Create Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl">
              <DialogHeader>
                <DialogTitle>Create New Organization</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Set up a new organization for your team
                </DialogDescription>
              </DialogHeader>
              <CreateOrganizationForm onSubmit={handleCreateOrg} formError={createFormError} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Personal Account Card */}
        <GlassPanel className="p-4 sm:p-6 mb-6 border-[#7c3aed] opacity-0 animate-expand-in [animation-fill-mode:forwards]" style={{ animationDelay: '0.2s' }}>
          {/* De pie, el boton de Settings era `flex-shrink-0` contra un bloque
              `flex-1`: cedia el nombre de la cuenta antes que el boton. Debajo
              de `sm` se apila y ocupa el ancho, que en un telefono es ademas
              donde se acierta con el pulgar. */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 
                            flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                  <h3 className="text-lg sm:text-xl font-semibold text-white break-words">
                    {personalAccount?.name || 'Personal Account'}
                  </h3>
                  <Badge className={`${
                    personalAccount?.is_active 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>
                    {personalAccount?.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </Badge>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  {personalAccount?.description || 'Your private workspace for personal projects'}
                </p>
                {/* Eran tres hijos en una rejilla de dos columnas, asi que el
                    badge caia solo en la segunda fila. Una fila que se parte
                    sola coloca los tres sin dejar huecos, y en movil los apila
                    en vez de partirlos en dos columnas de 130px. */}
                <div className="flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
                    <span className="text-gray-400">
                      Created <strong className="text-white">
                        {personalAccount?.created_at ? new Date(personalAccount.created_at).toLocaleDateString() : 'N/A'}
                      </strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-500 shrink-0" />
                    <span className="text-gray-400">
                      Tier: <strong className="text-white">
                        {loadingTier ? 'Loading...' : tierInfo ? (tierInfo.display_name || tierInfo.tier_type || 'N/A') : 'N/A'}
                      </strong>
                    </span>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 w-fit">
                    {personalAccount?.type || 'PERSONAL'}
                  </Badge>
                </div>
              </div>
            </div>
            {/* Administrar tu cuenta no es administrar tus organizaciones: la
                cuenta personal no pertenece a ninguna. Ahora lleva a su sección
                en vez de abrir un diálogo desde la pantalla de otra cosa. */}
            <Button
              variant="outline"
              onClick={() => personalAccount?.id && navigate(`/a/${personalAccount.id}/general`)}
              disabled={!personalAccount?.id}
              className="w-full sm:w-auto bg-black/40 border-white/20 text-white hover:bg-white/5 flex-shrink-0"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          </div>
        </GlassPanel>

        {/* Organizations List */}
        {organizationsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" color="purple" />
          </div>
        ) : error ? (
          <GlassPanel className="p-12 text-center">
            <p className="text-red-400">Error loading organizations: {error.join(', ')}</p>
          </GlassPanel>
        ) : organizations.length === 0 ? (
          <GlassPanel className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-xl font-semibold text-white mb-2">No organizations yet</h3>
            <p className="text-gray-400">
              Create your first organization to collaborate with your team
            </p>
          </GlassPanel>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {organizations.map((org) => (
              <OrganizationCard 
                key={org.id}
                organization={org}
                onDelete={handleDeleteOrg}
                onEdit={handleEditOrg}
                onSelect={handleSelectOrg}
              />
            ))}
          </div>
        )}
      </div>
      {/* Edit Organization Dialog */}
      <Dialog open={editModalOpen} onOpenChange={(open) => { setEditModalOpen(open); if (!open) setEditFormError(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update your organization details
            </DialogDescription>
          </DialogHeader>
          {selectedOrg && (
            <EditOrganizationForm 
              organization={selectedOrg}
              onSubmit={handleUpdateOrg}
              formError={editFormError}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deactivateConfirm}
        title="Deactivate Organization"
        message="This will deactivate the organization. You can reactivate it later from settings. Continue?"
        confirmText="Deactivate"
        cancelText="Cancel"
        variant="warning"
        onConfirm={confirmDeactivate}
        onCancel={cancelDeactivate}
      />

      {/* New Organization API Key Dialog */}
      <AlertDialog open={showNewOrgKeyDialog} onOpenChange={setShowNewOrgKeyDialog}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">Your Organization API Key</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This is your only chance to see the complete API key. Make sure to save it securely.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-black/40 border border-white/10 rounded-lg p-4">
              <code className="text-sm text-green-400 break-all font-mono">{newOrgApiKey}</code>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(newOrgApiKey);
                  setStatusMessage({ type: 'success', text: 'API Key copied to clipboard!' });
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy to Clipboard
              </Button>
              <Button
                onClick={() => {
                  const text = `Zenit AI Lab Organization API Key\n\nAPI Key: ${newOrgApiKey}\nGenerated: ${new Date().toISOString()}\n\nIMPORTANT: Keep this key secure. You won't be able to see it again.`;
                  const blob = new Blob([text], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `zenit-org-api-key-${Date.now()}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="flex-1 bg-primary hover:bg-violet-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download as File
              </Button>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-yellow-400 text-sm font-medium">
                ⚠️ Warning: Once you close this window, you won't be able to see the complete key again.
              </p>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogAction asChild>
              <Button 
                onClick={() => {
                  setShowNewOrgKeyDialog(false);
                  setNewOrgApiKey(null);
                }} 
                className="bg-gray-700 hover:bg-gray-800 text-white w-full"
              >
                I've Saved My Key
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
      </div>
    </section>
  );
}

/**
 * Organization Card Component
 */
function OrganizationCard({ organization, onDelete, onEdit, onSelect }) {
  console.log('🏢 Organization card data:', organization);
  
  const [membersModalOpen, setMembersModalOpen] = React.useState(false);
  
  // Por defecto, si creaste la org, eres owner
  const role = organization.role || 'owner';
  const canManage = ['owner', 'admin'].includes(role);

  // Se nombran porque el numero decide la palabra que va al lado, y "1 projects"
  // se leia en cuatro de las seis tarjetas.
  const memberCount = organization.total_members || 0;
  const projectCount = organization.total_projects || 0;

  const getRoleIcon = (role) => {
    switch(role) {
      case 'owner': return <Crown className="w-4 h-4 text-purple-400" />;
      case 'admin': return <Shield className="w-4 h-4 text-blue-400" />;
      case 'member': return <Users className="w-4 h-4 text-gray-400" />;
      case 'viewer': return <Eye className="w-4 h-4 text-yellow-400" />;
      default: return <Users className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      owner: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      member: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      viewer: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };

    return (
      <Badge className={`${roleColors[role] || roleColors.member}`}>
        <span className="mr-1">{getRoleIcon(role)}</span>
        {role?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <GlassPanel className="p-4 sm:p-6 hover:border-[#7c3aed] transition-colors">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-12 h-12 shrink-0 rounded-lg flex items-center justify-center text-white font-bold text-2xl">
              {organization.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-white break-words">{organization.name}</h3>
              <p className="text-sm text-gray-400 truncate">{organization.domain || 'No domain'}</p>
            </div>
          </div>
          <div className="shrink-0">{getRoleBadge(role)}</div>
        </div>

        {/* Description */}
        {organization.description && (
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">
            {organization.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-white">{memberCount}</span>
            <span className="text-gray-400">{memberCount === 1 ? 'member' : 'members'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-white">{projectCount}</span>
            <span className="text-gray-400">{projectCount === 1 ? 'project' : 'projects'}</span>
          </div>
        </div>

        {/* Meta Info */}
        <div className="space-y-2 mb-4 text-xs text-gray-400">
          {organization.contact_email && (
            <div className="flex items-center gap-2">
              <Mail className="w-3 h-3" />
              {organization.contact_email}
            </div>
          )}
          {organization.website && (
            <div className="flex items-center gap-2">
              <Globe className="w-3 h-3" />
              {organization.website}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            Created {new Date(organization.created_at).toLocaleDateString()}
          </div>
        </div>

        {/* Acciones. Las secundarias llevan su nombre escrito y no solo el
            icono: la unica pista era el `title`, y en un telefono no hay
            tooltip. Importa sobre todo en la ultima, que desactiva una
            organizacion con sus miembros adentro — estaba a un toque, sin
            etiqueta, en el borde donde cae el pulgar.

            "Open" ocupa el ancho de pie y las tres bajan a su propia fila. En
            escritorio la fila es una sola: hay sitio y la accion principal no
            necesita reclamarlo. Se renderizan una sola vez; lo que cambia es
            como se reparte el ancho. */}
        <div className="flex flex-col gap-2 mt-auto sm:flex-row">
          <Button
            onClick={() => onSelect(organization)}
            className="w-full sm:w-auto sm:flex-1 bg-primary hover:bg-violet-700 text-white"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Open
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setMembersModalOpen(true)}
              variant="outline"
              title="View members"
              className="flex-1 sm:flex-none bg-black/40 border-white/20 text-white hover:bg-white/5"
            >
              <Users className="w-4 h-4 sm:mr-0 mr-2" />
              <span className="sm:sr-only">Members</span>
            </Button>
            {canManage && (
              <>
                <Button
                  onClick={() => onEdit(organization)}
                  variant="outline"
                  title="Edit organization"
                  className="flex-1 sm:flex-none bg-black/40 border-white/20 text-white hover:bg-white/5"
                >
                  <Edit className="w-4 h-4 sm:mr-0 mr-2" />
                  <span className="sm:sr-only">Edit</span>
                </Button>
                {role === 'owner' && (
                  <Button
                    onClick={() => onDelete(organization.id)}
                    variant="outline"
                    title="Deactivate organization"
                    className="flex-1 sm:flex-none bg-black/40 border-white/20 text-orange-400 hover:bg-orange-500/20 hover:border-orange-500"
                  >
                    <XCircle className="w-4 h-4 sm:mr-0 mr-2" />
                    <span className="sm:sr-only">Deactivate</span>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Members Dialog */}
      <Dialog open={membersModalOpen} onOpenChange={setMembersModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/20 text-white max-w-3xl backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {organization.name} - Members
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Manage team members and their roles
            </DialogDescription>
          </DialogHeader>
          <OrganizationMembers 
            organizationId={organization.id}
            organizationName={organization.name}
            currentUserRole={role}
            onClose={() => setMembersModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </GlassPanel>
  );
}

/**
 * Create Organization Form Component
 */
function CreateOrganizationForm({ onSubmit, formError }) {
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    description: '',
    contact_email: '',
    website: '',
    industry: '',
    size: '1-10',
    country: ''
  });
  const [errors, setErrors] = useState({});

  const validateWebsite = (url) => {
    if (!url || url.trim() === '') return true;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (formData.website && !validateWebsite(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-white mb-2 text-sm">Organization Name *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Acme Corporation"
          required
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">
          Domain
          <span className="text-gray-400 text-xs ml-2">(e.g., company.com - no http://)</span>
        </label>
        <Input
          value={formData.domain}
          onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
          placeholder="company.com"
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of your organization..."
          rows={3}
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-white mb-2 text-sm">Contact Email</label>
          <Input
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            placeholder="contact@acme.com"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm">Company Size</label>
          <select
            value={formData.size}
            onChange={(e) => setFormData({ ...formData, size: e.target.value })}
            className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white"
          >
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">
          Website
          <span className="text-gray-400 text-xs ml-2">(must start with http:// or https://)</span>
        </label>
        <Input
          value={formData.website}
          onChange={(e) => {
            setFormData({ ...formData, website: e.target.value });
            if (errors.website) setErrors({ ...errors, website: null });
          }}
          placeholder="https://acme.com"
          className={`bg-black/40 border-white/20 text-white ${errors.website ? 'border-red-500' : ''}`}
        />
        {errors.website && (
          <p className="text-red-400 text-xs mt-1">{errors.website}</p>
        )}
      </div>

      {formError && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-400 text-sm">
          <span className="mt-0.5">⚠️</span>
          <span>{formError}</span>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button 
          type="submit"
          className="flex-1 bg-primary hover:bg-violet-700 text-white"
        >
          Create Organization
        </Button>
      </div>
    </form>
  );
}

/**
 * Edit Organization Form Component
 */
export function EditOrganizationForm({ organization, onSubmit, formError }) {
  const [formData, setFormData] = useState({
    name: organization.name || '',
    domain: organization.domain || '',
    description: organization.description || '',
    contact_email: organization.contact_email || '',
    website: organization.website || '',
    industry: organization.industry || '',
    size: organization.size || '1-10',
    country: organization.country || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-white mb-2 text-sm">Organization Name *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Acme Corporation"
          required
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">
          Domain
          <span className="text-gray-400 text-xs ml-2">(e.g., company.com - no http://)</span>
        </label>
        <Input
          value={formData.domain}
          onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
          placeholder="company.com"
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of your organization..."
          rows={3}
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-white mb-2 text-sm">Contact Email</label>
          <Input
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            placeholder="contact@acme.com"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm">Company Size</label>
          <select
            value={formData.size}
            onChange={(e) => setFormData({ ...formData, size: e.target.value })}
            className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white"
          >
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">Website</label>
        <Input
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://acme.com"
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-white mb-2 text-sm">Industry</label>
          <Input
            value={formData.industry}
            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
            placeholder="Technology"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm">Country</label>
          <Input
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            placeholder="USA"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>
      </div>

      {formError && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-400 text-sm">
          <span className="mt-0.5">⚠️</span>
          <span>{formError}</span>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button 
          type="submit"
          className="flex-1 bg-primary hover:bg-violet-700 text-white"
        >
          Update Organization
        </Button>
      </div>
    </form>
  );
}
